import React, { useState } from "react";
import {
  Modal,
  List,
  Typography,
  Divider,
  Upload,
  Checkbox,
  Button,
  UploadProps,
} from "antd";
import {
  FileDoneOutlined,
  BankOutlined,
  LaptopOutlined,
  TrophyOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import "./ResumeUploader.scss";
import { Userprofile } from "../../../../Entities/user-profile.entity";
import { useUserProfiles } from "../../../../Hooks/useUserProfiles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { openNotification } from "../../../../Components/Notification/Index";
import { ParsedResumeContent } from "../../../../Interface/Index";
import { useAILoader } from "../../../../Context/AILoaderContext/AILoaderContext";
import ProfileMatcherService from "../../../../Services/profile.matcher.service";
import ResumePreviewForm from "./ResumePreviewForm";
import { useProfileMatching } from "../../../../Hooks/useProfileMatching";
dayjs.extend(relativeTime);

const { Dragger } = Upload;

interface ResumeUploaderProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const data = [
  {
    title: "Educational qualification",
    icon: <FileDoneOutlined />,
  },
  {
    title: "Work experience",
    icon: <BankOutlined />,
  },
  {
    title: "Technology proficiency",
    icon: <LaptopOutlined />,
  },
  {
    title: "Achievements and awards",
    icon: <TrophyOutlined />,
  },
];

const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
];

const ResumeUploader: React.FC<ResumeUploaderProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<Userprofile | null>(
    null,
  );
  const [parsedResumeData, setParsedResumeData] =
    useState<ParsedResumeContent | null>(null);

  const { userProfiles } = useUserProfiles();
  const { setAiLoaderText, setIsShowAILoader, clearAILoader } = useAILoader();

  const handleClose = () => {
    setResumeFile(null);
    setSelectedProfile(null);
    setParsedResumeData(null);
    setIsOpen(false);
  };

  const { handleWithSelectedProfile } = useProfileMatching({
    onSuccess: handleClose,
  });

  const profileMatcherService = new ProfileMatcherService();

  const handleExistingUserProfileSelection = (profile: Userprofile) => {
    if (selectedProfile?._id === profile._id) {
      setSelectedProfile(null);
      return;
    }
    setSelectedProfile(profile);
  };

  const beforeUpload = (file: File): boolean => {
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const isValidType = ALLOWED_MIME_TYPES.includes(file.type);

    if (!isValidType) {
      openNotification(
        "error",
        "Invalid File Type",
        "Please upload a PDF, DOC or DOCX file only.",
      );
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      openNotification(
        "error",
        "File Too Large",
        "File size must be less than 5MB.",
      );
      return false;
    }

    return true;
  };

  const handleResumeFileUpload = async (file: File) => {
    setSelectedProfile(null);
    setResumeFile(file);
    setParsedResumeData(null);
    setIsShowAILoader(true);
    setAiLoaderText("Extracting resume content...");
    try {
      const data =
        await profileMatcherService.getFormattedResumeByResumeRawData(file);
      if (data) {
        setParsedResumeData(data);
        openNotification(
          "success",
          "Resume Extracted",
          "Resume content has been extracted successfully.",
        );
      } else {
        setParsedResumeData({
          basicDetails: {
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            address: "",
            whatsAppConsent: { consent: true, timestamp: new Date(), history: [] },
          },
          socialLinks: { linkedin: "", github: "", personalPortfolio: "" },
          summary: "",
          experience: [],
          education: [],
          skills: [],
          projects: [],
          certifications: [],
          achievements: [],
          languages: [],
          otherDetails: "",
        });
        openNotification(
          "warning",
          "Could Not Extract Resume Content",
          "We couldn't read your resume. Please fill in your details manually.",
        );
      }
    } catch (error) {
      console.error("Error while parsing resume", error);
      setParsedResumeData({
        basicDetails: {
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          address: "",
          whatsAppConsent: { consent: true, timestamp: new Date(), history: [] },
        },
        socialLinks: { linkedin: "", github: "", personalPortfolio: "" },
        summary: "",
        experience: [],
        education: [],
        skills: [],
        projects: [],
        certifications: [],
        achievements: [],
        languages: [],
        otherDetails: "",
      });
      openNotification(
        "warning",
        "Could Not Extract Resume Content",
        "We couldn't read your resume. Please fill in your details manually.",
      );
    } finally {
      clearAILoader();
    }
  };

  const uploadResumeFile = async (options: any) => {
    const { file, onSuccess, onError } = options;

    try {
      await handleResumeFileUpload(file as File);
      onSuccess?.();
    } catch (error) {
      onError?.(error);
    }
  };

  const handleReUpload = () => {
    setResumeFile(null);
    setSelectedProfile(null);
    setParsedResumeData(null);
  };

  const props: UploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    disabled: resumeFile ? true : selectedProfile ? true : false,
    maxCount: 1,
    accept: ".pdf,.doc,.docx",
    onChange(info) {
      if (info.fileList.length > 1) {
        openNotification(
          "error",
          "Multiple Files Detected",
          "Please upload only one file at a time.",
        );
        info.fileList = [info.fileList[0]];
        return;
      }

      const { status } = info.file;
      if (status === "done") {
        console.debug("File upload completed");
      } else if (status === "error") {
      }
    },
    onDrop(e) {
      if (e.dataTransfer.files.length > 1) {
        openNotification(
          "error",
          "Multiple Files Detected",
          "Please drop only one file at a time.",
        );
      }
    },
    beforeUpload,
    customRequest: uploadResumeFile,
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="resume-uploader-modal">
      <Modal
        open={isOpen}
        onCancel={handleClose}
        footer={null}
        centered
        closeIcon={false}
        width="100%"
        style={{ maxWidth: "1200px" }}
      >
        <div className="two-column-layout">
          <div className="two-column-layout__first">
            {!parsedResumeData && (
              <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Upload PDF, DOC or DOCX files. Maximum file size allowed is 5
                  MB.
                </p>
              </Dragger>
            )}

            {userProfiles.length > 0 && !parsedResumeData && (
              <Divider style={{ margin: "0" }}>or</Divider>
            )}

            {userProfiles.length > 0 && !parsedResumeData && (
              <Typography.Text
                strong
                style={{ display: "block", textAlign: "start" }}
              >
                Continue with previously uploaded resume
              </Typography.Text>
            )}

            {!parsedResumeData && (
              <div
                className={`resume-selector-file-list-items-container ${
                  resumeFile ? "disabled-container" : ""
                }`}
              >
                {userProfiles.map((profile, index) => (
                  <div
                    className={`resume-selector-file-list ${
                      selectedProfile?._id === profile._id
                        ? "resume-selector-file-list-selected"
                        : ""
                    }`}
                    key={index}
                    onClick={() => handleExistingUserProfileSelection(profile)}
                  >
                    <Checkbox
                      disabled={resumeFile ? true : false}
                      checked={selectedProfile?._id === profile._id}
                    />
                    <div className="resume-selector-file-list-item-content">
                      <Typography.Text disabled={resumeFile ? true : false}>
                        {profile.filename}
                      </Typography.Text>
                      <small>
                        Last used {dayjs(profile.lastUsedAt).fromNow()}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedProfile && !parsedResumeData && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Button
                  disabled={selectedProfile === null}
                  type="primary"
                  onClick={() => handleWithSelectedProfile(selectedProfile)}
                >
                  Continue with selected resume
                </Button>
              </div>
            )}

            {parsedResumeData && resumeFile && (
              <ResumePreviewForm
                parsedResumeData={parsedResumeData}
                handleReUpload={handleReUpload}
                handleClose={handleClose}
                selectedFile={resumeFile}
              />
            )}
          </div>
          <div className="two-column-layout__second">
            <Typography.Title
              level={5}
              style={{ margin: 0, textAlign: "center" }}
            >
              Don't forget to add the following details on your resume
            </Typography.Title>

            <Divider style={{ margin: "0" }} />

            <div>
              <List
                itemLayout="horizontal"
                size="small"
                bordered={false}
                dataSource={data}
                split={false}
                renderItem={(item, index) => (
                  <List.Item key={index}>
                    <List.Item.Meta
                      avatar={
                        <div className="resume-uploader-info-icon">
                          {item.icon}
                        </div>
                      }
                      title={
                        <Typography.Text
                          type="secondary"
                          style={{ fontWeight: 500 }}
                        >
                          {item.title}
                        </Typography.Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ResumeUploader;
